import { TextField, debounce, Autocomplete } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { appState } from "$reducers";
import {
  DockyardServices,
  ProductService,
  ProjectServices,
  ReceptionService,
} from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

type Props = {
  currentCompany?: any;
  onChange: any;
  value: any;
  isOrdersTab?: boolean;
};

function JCombineFilters({
  currentCompany,
  onChange,
  value,
  isOrdersTab = false,
}: Props) {
  const [currentSearch, setCurrentSearch] = useState(
    typeof value === "string" ? value : ""
  );
  const activeOptions = currentSearch.length >= 3;
  const { filter: companySidebarFilter } = useSelector(appState);
  const [productFilter, setProductFilter] = useState(null);
  const [projectFilter, setProjectFilter] = useState(null);
  const [dockyardFilter, setDockyardFilter] = useState(null);
  const [references, setReferences] = useState(null);

  // Stable debounce — recreating debounce() every render re-triggers Autocomplete
  // onInputChange and can cascade into Maximum update depth under React 19.
  const debouncedSetCurrentSearch = useMemo(
    () =>
      debounce((_e: any, data: string) => {
        setCurrentSearch(data ?? "");
      }, 500),
    []
  );

  const fetchProjects = useCallback(async () => {
    if (currentSearch?.length > 0) {
      const filters = {
        isActive: true,
        companyDisable: false,
        ...((companySidebarFilter || currentCompany) && {
          companyId: companySidebarFilter
            ? companySidebarFilter._id
            : currentCompany,
        }),
        ...(currentSearch && { nameCode: currentSearch }),
      };
      let getProject;
      getProject = await queryClient.getQueryData([
        CacheInvalidationAction.GET_PROJECTS,
        filters,
      ]);
      if (!getProject) {
        getProject = await queryClient.fetchQuery({
          queryKey: [CacheInvalidationAction.GET_PROJECTS, filters],
          queryFn: () => ProjectServices.getProjects(filters),
          staleTime: 1000 * 60,
        });
      }
      setProjectFilter(getProject);
    }
  }, [companySidebarFilter, currentCompany, currentSearch]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Products
  const fetchProducts = useCallback(async () => {
    if (currentSearch?.length >= 3) {
      const getProduct = await queryClient.fetchQuery({
        queryKey: [
          CacheInvalidationAction.GET_PRODUCT_BY_REFERENCE_AUTOCOMPLETE,
          companySidebarFilter ? companySidebarFilter._id : currentCompany,
          currentSearch,
        ],

        queryFn: () =>
          ProductService.getProductByReference({
            ...(currentSearch && { completeReference: currentSearch }),
            ...(companySidebarFilter && { company: companySidebarFilter._id }),
            ...(currentCompany && { company: currentCompany }),
          }),

        staleTime: 1000 * 60,
      });
      setProductFilter(getProduct);
    }
  }, [currentCompany, companySidebarFilter, setProductFilter, currentSearch]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const fetchReferences = useCallback(async () => {
    if (currentSearch?.length >= 3) {
      const id = companySidebarFilter
        ? companySidebarFilter._id
        : currentCompany;
      let references;
      references = await queryClient.getQueryData([
        CacheInvalidationAction.GET_REFERENCEMENTS,
        currentSearch,
      ]);
      if (!references) {
        references = await queryClient.fetchQuery({
          queryKey: [CacheInvalidationAction.GET_REFERENCEMENTS, currentSearch],
          queryFn: () => ReceptionService.getReferencements(id),
          staleTime: 1000 * 60,
        });
      }
      setReferences(references);
    }
  }, [currentCompany, companySidebarFilter, setReferences, currentSearch]);

  useEffect(() => {
    fetchReferences();
  }, [fetchReferences]);

  // Query Dockyards
  const fetchDockyards = useCallback(async () => {
    if (currentSearch?.length > 0) {
      const filters = {
        isActive: true,
        companyDisable: false,
        ...((companySidebarFilter || currentCompany) && {
          company: companySidebarFilter
            ? companySidebarFilter._id
            : currentCompany,
        }),
        ...(currentSearch && { nameCode: currentSearch }),
      };
      let getDockyard;
      getDockyard = await queryClient.getQueryData([
        CacheInvalidationAction.GET_DOCKYARDS,
        filters,
      ]);
      if (!getDockyard) {
        getDockyard = await queryClient.fetchQuery({
          queryKey: [CacheInvalidationAction.GET_DOCKYARDS, filters],
          queryFn: () => DockyardServices.getDockyards(filters),
          staleTime: 1000 * 60,
        });
      }
      setDockyardFilter(getDockyard);
    }
  }, [companySidebarFilter, currentCompany, currentSearch]);

  useEffect(() => {
    fetchDockyards();
  }, [fetchDockyards]);

  const globalSearchOptions = useMemo(() => {
    if (!activeOptions) {
      return [];
    }
    // Options constructor
    // Go to order backend to add new filter _XXXX
    let projectOptions = (projectFilter?.data || [])
      .filter(
        (p: any) =>
          p.status !== "done" &&
          !p.isDisable &&
          !p.companyDisable &&
          p.company.isActive
      )
      .map((o: any) => ({
        value: `project_${o._id}`,
        label: `${o.name} - ${o.code} - <Projet>`,
        labelName: o.name,
        labelCode: o.code,
        labelType: "Projet",
        company: o.company._id,
      }));
    projectOptions = companySidebarFilter
      ? projectOptions.filter(
          (projectOption: any) =>
            projectOption.company === companySidebarFilter?._id
        )
      : projectOptions;

    let dockyardOptions = (dockyardFilter?.data || [])
      .filter(
        (d: any) =>
          d.status !== "done" &&
          !d.isDisable &&
          !d.companyDisable &&
          !d.projectDisable &&
          d.company.isActive
      )
      .map((o: any) => ({
        value: `dockyard_${o._id}`,
        label: `${o.name} - ${o.code} - <Chantier>`,
        labelName: o.name,
        labelCode: o.code,
        labelType: "Chantier",
        company: o.company._id,
      }));
    dockyardOptions = companySidebarFilter
      ? dockyardOptions.filter(
          (dockyardOption: any) =>
            dockyardOption.company === companySidebarFilter?._id
        )
      : dockyardOptions;

    let productOptions = (productFilter?.data || [])
      .filter((p: any) => p.isReferenced === true)
      .map((o: any) => ({
        value: `product_${o?.reference}`,
        label: `${o.designation} - ${o.reference} - <Produit>`,
        labelName: o.designation,
        labelCode: o.reference,
        labelType: "Produit",
        company: o.company,
      }));
    productOptions = companySidebarFilter
      ? productOptions.filter(
          (productOption: any) =>
            productOption.company === companySidebarFilter?._id
        )
      : productOptions;

    let referenceOptions = (references || []).map((ref: any) => ({
      value: isOrdersTab ? `reference_${ref.order}` : `reference_${ref.id}`,
      label: `${ref.reference} - ${ref.order} - <Référence>`,
      labelName: ref.reference,
      labelCode: ref.order,
      labelType: "Référence",
      company: ref.company,
    }));
    referenceOptions = companySidebarFilter
      ? referenceOptions.filter(
          (ref: any) => ref.company === companySidebarFilter?._id
        )
      : referenceOptions;

    if (currentCompany) {
      projectOptions = projectOptions.filter(
        (p: any) => p.company === currentCompany
      );
      dockyardOptions = dockyardOptions.filter(
        (d: any) => d.company === currentCompany
      );
      productOptions = productOptions.filter(
        (p: any) => p.company === currentCompany
      );
      referenceOptions = referenceOptions.filter(
        (r: any) => r.company === currentCompany
      );
    }

    let globalSearchOptions = projectOptions.concat(
      dockyardOptions.concat(productOptions.concat(referenceOptions))
    );

    // Remove duplicate
    globalSearchOptions = globalSearchOptions.filter(
      (o: any, i: number) =>
        globalSearchOptions.findIndex((opt: any) => opt.value === o.value) === i
    );
    return globalSearchOptions;
  }, [
    activeOptions,
    companySidebarFilter,
    currentCompany,
    projectFilter,
    dockyardFilter,
    productFilter,
    references,
    isOrdersTab,
  ]);

  const currentOption = globalSearchOptions.find(
    (option: any) => option.value === value
  );

  return (
    <div style={{ marginRight: 16 }}>
      <Autocomplete
        options={globalSearchOptions || []}
        aria-readonly={false as boolean}
        getOptionLabel={(option: any) => option.label}
        renderOption={(props: any, option: any) => (
          <div
            {...props}
            style={{
              display: "flex",
              flexDirection: "row",
            }}
          >
            {option.labelName}
            <div
              style={{
                color: "grey",
                marginLeft: 5,
              }}
            >{`- ${option.labelCode}`}</div>
            <div
              style={{
                color: "grey",
                marginLeft: 5,
                fontStyle: "italic",
                opacity: "0.7",
              }}
            >{`- ${option.labelType}`}</div>
          </div>
        )}
        onInputChange={debouncedSetCurrentSearch}
        onChange={(e, data) => {
          onChange(data?.value);
        }}
        value={currentOption ?? null}
        renderInput={(params: any) => (
          <TextField
            {...params}
            placeholder="Rechercher par chantier, projet ou produit"
            variant="outlined"
            style={{
              marginRight: 16,
              minWidth: 370,
              minHeight: 37,
            }}
          />
        )}
        noOptionsText="Pas de résultat"
      />
    </div>
  );
}

export default JCombineFilters;
